import {
  PRO_USER_CORE_ROLE,
  SERVICE_TIER_FREEMIUM,
  SERVICE_TIER_PRO,
  SERVICE_TIER_PUBLIC,
  ServiceTier,
} from '../constants/Constants';
import { isInRole } from '../jwt/JwtReader';
import { LogDebug, LogMsg } from './Logging';
import { IEmAuthCookies, ILoggerDetails, IEmServiceDetails } from '../types/types';
import { hasPublicAlternativeService, supportsProUsers, supportsFreemiumUsers } from './TierHelper';
import { constructUrl } from '../utils/urlUtils';

/**
 * Retrieves the route for a specific service tier from the service details.
 * @param {IEmServiceDetails} emServiceDetails - The service details containing tier-to-route mappings
 * @param {ServiceTier} serviceTier - The service tier to find the route for
 * @returns {string | null} - The route for the specified tier, or null if not found
 */
export function getRouteByTier(emServiceDetails: IEmServiceDetails, serviceTier: ServiceTier): string | null {
  if (!emServiceDetails?.tierToRouteMapping || emServiceDetails.tierToRouteMapping.length === 0) {
    return null;
  }

  for (const mapping of emServiceDetails.tierToRouteMapping) {
    if (mapping.tiers.includes(serviceTier)) {
      return mapping.route;
    }
  }

  return null;
}

/**
 * Get first-to-find route mapped for logged-in tier (i.e. PRO or FREEMIUM)
 * @param emServiceDetails
 * @returns route if found, null otherwise
 */
export function getFirstOrDefaultLoggedInHost(emServiceDetails: IEmServiceDetails): string | null {
  if (!emServiceDetails?.tierToRouteMapping || emServiceDetails.tierToRouteMapping.length === 0) {
    return null;
  }

  for (const mapping of emServiceDetails.tierToRouteMapping) {
    if (mapping.tiers.includes(SERVICE_TIER_FREEMIUM) || mapping.tiers.includes(SERVICE_TIER_PRO)) {
      return mapping.route;
    }
  }

  return null;
}

export interface GetLoggedInUrlFromPublicUrlParams {
  /** The public URL to be converted. */
  publicUrl: string;
  /** New host to use. */
  newHost: string;
  /** (Optional) Logger for logging information. */
  logger?: ILoggerDetails;
}

export function getLoggedInUrlFromPublicUrl(
  publicUrl: string, // NO "null", "", "undefined" allowed - consuming function must do that checking
  newHost: string, // NO "null", "", "undefined" allowed - consuming function must do that checking
  logger?: ILoggerDetails
): string | null {
  // Get current URL in a proper format so we can compare if the user is in the right place
  const currUrl = new URL(publicUrl);
  const currentHostWithProtocol = `${currUrl.protocol}//${currUrl.host}`;

  const expectedRouteUrl = new URL(newHost);
  const expectedHostWithProtocol = `${expectedRouteUrl.protocol}//${expectedRouteUrl.host}`;

  LogDebug(logger, `getLoggedInUrlFromPublicUrl() :: currentUrl :: ${currUrl}, newHost: ${expectedHostWithProtocol}`);

  if (currentHostWithProtocol === expectedHostWithProtocol) {
    LogDebug(logger, `getLoggedInUrlFromPublicUrl() :: current and new URLs are the same: ${currentHostWithProtocol}`);
    return null; // User is already on the correct URL, no need to redirect
  }

  // Extract the path segments, i.e. the service (e.g. em.com..->>/forecasts/) and the rest of the URL
  const pathSegments = currUrl.pathname.split('/').filter((chunk) => chunk);
  if (pathSegments.length === 0) {
    return null;
  }

  let serviceName = pathSegments[0];
  LogDebug(logger, `service :: ${serviceName}`);
  if (!serviceName) {
    LogMsg(logger, `getLoggedInUrlFromPublicUrl() :: no service subroute, returning empty string`);
    return null;
  }

  // Get the path after "service name" (e.g. /forecasts...->>/collectionid/templateid?param=dragons)
  const newPathAndQuery =
    currUrl.pathname.replace(`/${serviceName}/`, '').replace(`/${serviceName}`, '') + currUrl.search;

  const proUrl = constructUrl(expectedHostWithProtocol, newPathAndQuery || '');

  return proUrl;
}

export interface GetPublicUrlFromLoggedInUrlParams {
  /** The PRO (logged-in) URL to be converted. */
  loggedInUrl: string;
  /** The service details object. */
  emService: IEmServiceDetails;
  /** (Optional) Logger for logging information. */
  logger?: ILoggerDetails;
}

/**
 * Converts a PRO/Freemium (logged-in) URL to its corresponding public URL.
 * Takes into account the service type and environment subdomain, and constructs the appropriate public URL, preserving query parameters.
 *
 * For localhost development (emService.currentEnvironment === ENV_LOCAL):
 * - If hasAltService is true: returns {current-host:current-port}/em_auth-localhost-redirect-to-public
 * - If hasAltService is false: returns {current-host:current-port}/em_auth-localhost-redirect-to-reauthentication
 *
 * NOTE: Returns empty string if: 1) the PRO/Freemium URL is for the homepage, 2) the service doesn't have a matching public service
 *
 * @param {Object} params - Object containing all parameters needed for URL mapping
 * @param {string} params.loggedInUrl - The PRO/Freemium (logged-in) URL to be converted.
 * @param {IEmServiceDetails} params.emService - The service details object.
 * @param {ILoggerDetails} [params.logger] - (Optional) Logger for logging information.
 * @returns {string} - The corresponding public URL.
 */
export function getPublicUrlFromLoggedInUrl({
  loggedInUrl,
  emService,
  logger,
}: GetPublicUrlFromLoggedInUrlParams): string {
  LogDebug(logger, `getPublicUrlFromLoggedInUrl() called with URL: ${loggedInUrl}`);

  if (!loggedInUrl) {
    LogMsg(logger, `getPublicUrlFromLoggedInUrl() called with **empty** URL`);
    throw new Error('getPublicUrlFromLoggedInUrl() :: url not provided');
  }

  if (!hasPublicAlternativeService(emService)) {
    LogMsg(logger, `getPublicUrlFromLoggedInUrl() called without defined public tier alternative`);
    throw new Error('getPublicUrlFromLoggedInUrl() :: called without defined public tier alternative');
  }

  const iUrl = new URL(loggedInUrl);

  const publicHost = getRouteByTier(emService, SERVICE_TIER_PUBLIC);

  if (!publicHost) {
    LogMsg(logger, `getPublicUrlFromLoggedInUrl() :: no public route found`);
    throw new Error('getPublicUrlFromLoggedInUrl() :: no public route found');
  }

  // Add path and query string if present
  const publicUrl = constructUrl(publicHost, `${iUrl.pathname}${iUrl.search}`);
  LogDebug(logger, `getPublicUrlFromLoggedInUrl() :: publicUrl :: ${publicUrl}`);

  return publicUrl;
}

export interface GetRedirectPodUrlByRoleParams {
  /** The original URL to check and potentially redirect. */
  originalUrl: string;
  /** The user's authentication cookies. */
  emAuthCookies: IEmAuthCookies;
  /** The service details object. */
  emService: IEmServiceDetails;
  /** (Optional) Logger for logging information. */
  logger?: ILoggerDetails;
}

/**
 * Determines if a service pod URL needs to be redirected based on user role.
 * Checks if the URL is a service pod URL (format: [service]-pod) and redirects to the appropriate pod based on user's role-tier.
 * Supports various services like content, chart, topics, etc.
 *
 * For localhost development (emService.currentEnvironment === ENV_LOCAL):
 * - Returns HTML response explaining why redirection is needed but cannot be performed
 *
 * @param {Object} params - Object containing all parameters needed for pod redirection
 * @param {string} params.originalUrl - The original URL to check and potentially redirect.
 * @param {IEmAuthCookies} params.emAuthCookies - The user's authentication cookies.
 * @param {IEmServiceDetails} params.emService - The service details object.
 * @param {ILoggerDetails} [params.logger] - (Optional) Logger for logging information.
 * @returns {string | null} - The redirect URL if redirection is needed, null if no redirection required or URL is not a service pod URL.
 */
export function getRedirectPodUrlByRole({
  originalUrl,
  emAuthCookies,
  emService,
  logger,
}: GetRedirectPodUrlByRoleParams): string | null {
  LogDebug(logger, `getRedirectPodUrlByRole() called with URL: ${originalUrl}`);

  if (!originalUrl) {
    LogDebug(logger, `getRedirectPodUrlByRole() called with empty URL`);
    throw new Error('getRedirectPodUrlByRole() :: Missing originalUrl');
  }

  if (!emAuthCookies?.em_at) {
    LogDebug(logger, `getRedirectPodUrlByRole() called with missing em_at`);
    throw new Error('getRedirectPodUrlByRole() :: Missing em_at');
  }

  const isProUser = isInRole(emAuthCookies.em_at, PRO_USER_CORE_ROLE);

  let isUserOnRightPod = false; // by default we assume user is on the wrong pod for their role (aka. needs redirect to different pod), unless proven otherwise
  if (isProUser) {
    isUserOnRightPod = supportsProUsers(emService);
  } else {
    isUserOnRightPod = supportsFreemiumUsers(emService);
  }

  if (isUserOnRightPod) {
    return null; // user is on the correct pod (aka. current service supports user's role), so no need to redirect anywhere
  }

  /** */
  /***** FROM HERE AND ONWARD WE ASSUME USER IS ON THE POD THAT DOESN'T SUPPORT THEIR USER TIER *****/
  /** */

  const oriUrl = new URL(originalUrl);
  const proHost = getRouteByTier(emService, isProUser ? SERVICE_TIER_PRO : SERVICE_TIER_FREEMIUM);

  if (!proHost) {
    LogDebug(
      logger,
      `getRedirectPodUrlByRole() :: no route found for tier: ${isProUser ? SERVICE_TIER_PRO : SERVICE_TIER_FREEMIUM}`
    );
    return null;
  }

  const newUrl = new URL(constructUrl(proHost, `${oriUrl.pathname}${oriUrl.search}`));

  if (newUrl.toString() !== oriUrl.toString()) {
    return newUrl.toString();
  }

  // no redirect needed
  return null;
}
